import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { CompetitorMgItemPrice } from '@retail/products/types';
import { EmptyCircularFallbackImage, Image } from '@retail/components';
import { useProductsTFunction } from '@retail/products/i18n';
import { formatPrice } from '@retail/products/utils';
import { ExternalLinkIcon } from '@shared/custom-icons';
import { EquivalentItemTooltip } from './EquivalentItemTooltip';

export interface CompetitorPriceRowProps {
  competitorPrice: CompetitorMgItemPrice;
  logoUrl: string;
}

export function CompetitorPriceRow({ competitorPrice, logoUrl }: CompetitorPriceRowProps) {
  const { i18n } = useTranslation();
  const t = useProductsTFunction();
  const {
    mgItemNumber,
    equivalentMgItemNumber,
    storeName,
    url,
    price,
    lastObserved,
    previousPrice,
    previousLastObserved,
    unit,
  } = competitorPrice;

  const formatUnitPrice = (value: number) =>
    t('products.prices.pricePerUnit', {
      price: formatPrice(value, i18n.language),
      unit: unit ?? '',
    });

  const priceChange = price && previousPrice ? ((price - previousPrice) / previousPrice) * 100 : 0;
  const showEquivalent = equivalentMgItemNumber && equivalentMgItemNumber !== mgItemNumber;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={2}
      px={2}
      py={1.25}
      sx={(theme) => ({
        transition: theme.transitions.create('background-color'),
        '&:hover': { bgcolor: 'action.hover' },
      })}
    >
      <Image src={logoUrl} fallback={() => <EmptyCircularFallbackImage size="32px" />} />

      <Stack flex={1} minWidth={0}>
        <Typography
          variant="body1"
          fontWeight={500}
          color={price ? 'text.primary' : 'text.secondary'}
          noWrap
        >
          {storeName}
        </Typography>
        {price && lastObserved && (
          <Typography variant="caption" color="text.secondary">
            {dayjs(lastObserved).format('ll')}
          </Typography>
        )}
      </Stack>

      {price ? (
        <Stack alignItems="flex-end" flexShrink={0}>
          <Stack direction="row" alignItems="center" gap={0.75}>
            {showEquivalent && <EquivalentItemTooltip mgItemNumber={equivalentMgItemNumber} />}
            <Typography variant="subtitle1" fontWeight={600}>
              {formatUnitPrice(price)}
            </Typography>
            {url && (
              <Box
                component="a"
                href={url}
                target="_blank"
                display="flex"
                color="text.secondary"
                sx={{ '&:hover': { color: 'primary.main' } }}
              >
                <ExternalLinkIcon
                  fontSize="inherit"
                  titleAccess={t('products.productList.competitorPrice.openInNew')}
                />
              </Box>
            )}
          </Stack>
          {!!priceChange && previousPrice && (
            <Tooltip
              arrow
              placement="left"
              title={
                <Stack alignItems="flex-end">
                  <Typography variant="caption">
                    {t('products.prices.competitors.historic.lastPrice')}
                  </Typography>
                  <Typography variant="subtitle2">{formatUnitPrice(previousPrice)}</Typography>
                  {previousLastObserved && (
                    <Typography variant="caption">
                      {dayjs(previousLastObserved).format('ll')}
                    </Typography>
                  )}
                </Stack>
              }
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                color={priceChange > 0 ? 'success.main' : 'error.main'}
              >
                {priceChange > 0 ? (
                  <TrendingUp sx={{ fontSize: 16 }} />
                ) : (
                  <TrendingDown sx={{ fontSize: 16 }} />
                )}
                <Typography variant="caption" fontWeight={600}>
                  {`${priceChange > 0 ? '+' : ''}${priceChange.toLocaleString(i18n.language, {
                    maximumFractionDigits: 1,
                  })} %`}
                </Typography>
              </Stack>
            </Tooltip>
          )}
        </Stack>
      ) : (
        <Typography variant="subtitle1" color="text.disabled">
          –
        </Typography>
      )}
    </Stack>
  );
}
